import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import API_URLS from '../../../api/api_urls';
import { PlayerInfo, TeamInfo } from '../../../api/types';
import ROUTER_URLS from '../../utils/router/urls';
import EXTERNAL_URLS from '../../utils/router/external_urls';
import { useFetchPlayers, useFetchTeams } from '../../hooks/useFetch';
import { LeagueType, useLeagueContext } from '../../contexts/LeagueContext';
import SearchDebounce from '../../utils/common/search';
import formatPlayerSeasons from '../../utils/common/playerSeasons';

type SearchResultOption =
  | { kind: 'team'; data: TeamInfo }
  | { kind: 'player'; data: PlayerInfo };

const LEAGUES: LeagueType[] = ['nba', 'wnba', 'ncaambb', 'ncaawbb'];

// Only NCAAM/NCAAW have in-app player/team profile pages today; NBA/WNBA profiles
// still live on the legacy site, so those leagues link out via EXTERNAL_URLS instead.
const LEAGUES_WITH_INTERNAL_PROFILES: LeagueType[] = ['ncaambb', 'ncaawbb'];

// NBA/WNBA's player manifest has no first_year/last_year/is_active/is_freshman data
// (the Finder pages' EliasNameFilter never passes displayPlayerInfo for those leagues
// either), so there's nothing to show there - only NCAAM/NCAAW players have seasons.
const LEAGUES_WITH_PLAYER_YEARS: LeagueType[] = ['ncaambb', 'ncaawbb'];

const EXTERNAL_LEAGUE_KEYS: Record<LeagueType, keyof typeof EXTERNAL_URLS> = {
  nba: 'NBA',
  wnba: 'WNBA',
  ncaambb: 'NCAAM',
  ncaawbb: 'NCAAW',
};

const getLeagueKey = (selectedLeague: unknown): LeagueType => {
  const normalized = typeof selectedLeague === 'string' ? selectedLeague.toLowerCase() : '';
  return (LEAGUES as string[]).includes(normalized) ? (normalized as LeagueType) : 'nba';
};

const StyledSearch = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '0px 8px 8px 0px',
  backgroundColor: alpha('#1570ef', 0.3),
  '&:hover': {
    backgroundColor: alpha('#1570ef', 0.4),
  },
  marginRight: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const StyledSearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  minWidth: 320,
  '& .MuiOutlinedInput-root': {
    color: theme.palette.common.white,
    paddingLeft: `calc(1em + ${theme.spacing(4)}) !important`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiAutocomplete-clearIndicator': {
    color: theme.palette.common.white,
  },
  '& input::placeholder': {
    color: theme.palette.common.white,
    opacity: 1,
  },
}));

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  padding: '6px 16px',
  fontWeight: 600,
  color: theme.palette.custom.darkGrey,
  borderBottom: `1px solid ${theme.palette.custom.lightestGrey}`,
  backgroundColor: theme.palette.custom.white,
}));

function getOptionLabel(option: SearchResultOption): string {
  if (option.kind === 'team') {
    return (option.data as any).short_name || option.data.full_name;
  }
  return option.data.display_name;
}

function Search() {
  const leagueContext: any = useLeagueContext();
  const navigate = useNavigate();
  const leagueKey = getLeagueKey(leagueContext?.selectedLeague);
  const displayPlayerYears = LEAGUES_WITH_PLAYER_YEARS.includes(leagueKey);

  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  // The league the debounced searchValue was issued under. A league switch changes
  // leagueKey (and therefore the manifest URLs) on the very next render, before the
  // effect below has a chance to clear searchValue - if enabled only checked
  // searchValue, that render would fire a request for the old term against the new
  // league's endpoints. Gating on a match here prevents that discarded fetch outright.
  const [searchLeague, setSearchLeague] = useState<LeagueType>(leagueKey);

  const searchDebounce = useMemo(() => new SearchDebounce(), []);
  useEffect(() => () => searchDebounce.clear(), [searchDebounce]);

  // Drop any in-flight search when the league changes so stale results from the
  // previous league never carry over into the new one. Cancel the pending debounce
  // too, otherwise it can still fire and write the old league's query back in.
  useEffect(() => {
    searchDebounce.clear();
    setInputValue('');
    setSearchValue('');
    setSearchLeague(leagueKey);
  }, [leagueKey, searchDebounce]);

  const leagueApiUrls = API_URLS[leagueKey];
  const searchEnabled = searchValue.trim().length > 0 && searchLeague === leagueKey;

  // Scope this component's queries separately from page-level filters (e.g. the
  // Finder pages' TeamFilter/EliasNameFilter) that fetch unconditionally against the
  // same manifest endpoints - otherwise this search would share their cache entry and
  // show "loading" for however long their unrelated, unfiltered fetch takes.
  const { data: teamData, isFetching: isFetchingTeams, isError: isTeamsError } = useFetchTeams(
    searchValue,
    leagueApiUrls.franchise_season_manifest,
    searchEnabled,
    'header-search',
  );
  const { data: playerData, isFetching: isFetchingPlayers, isError: isPlayersError } = useFetchPlayers(
    searchValue,
    leagueApiUrls.players_manifest,
    searchEnabled,
    'header-search',
  );

  // With keepPreviousData, teamData/playerData can still hold the previous query's
  // results while the input has moved on (debounce pending), a request for the
  // current query is still in flight, or that request just failed. Only surface
  // options once the debounced query matches what's on screen and both requests
  // have settled on it without error.
  const isSearchSettled = inputValue === searchValue
    && !isFetchingTeams && !isFetchingPlayers
    && !isTeamsError && !isPlayersError;

  const options: SearchResultOption[] = useMemo(() => {
    if (!searchEnabled || !isSearchSettled) {
      return [];
    }
    const teams: SearchResultOption[] = (teamData?.results ?? []).map((data) => ({ kind: 'team', data }));
    const players: SearchResultOption[] = (playerData?.results ?? []).map((data) => ({ kind: 'player', data }));
    return [...teams, ...players];
  }, [teamData, playerData, searchEnabled, isSearchSettled]);

  const handleInputChange = (_event: any, newInputValue: string, reason: AutocompleteInputChangeReason) => {
    // MUI fires 'reset' with the selected option's label after a selection (and after
    // handleChange has already navigated), and 'clear' when the clear icon is clicked.
    // Neither is user typing, so don't treat it as a new search term.
    if (reason !== 'input') {
      searchDebounce.clear();
      setInputValue('');
      setSearchValue('');
      return;
    }

    setInputValue(newInputValue);
    if (newInputValue !== inputValue) {
      // Capture the league this term was typed under so a switch that happens
      // before the debounce fires doesn't attach the old term to the new league.
      const searchedLeague = leagueKey;
      searchDebounce.debounce(() => {
        setSearchValue(newInputValue);
        setSearchLeague(searchedLeague);
      });
    }
  };

  const handleChange = (_event: any, value: unknown) => {
    const newValue = value as SearchResultOption | null;
    if (!newValue) {
      return;
    }

    const isInternal = LEAGUES_WITH_INTERNAL_PROFILES.includes(leagueKey);

    if (newValue.kind === 'team') {
      const teamId = String(newValue.data.franchise_id);
      if (isInternal) {
        navigate(generatePath(ROUTER_URLS[leagueKey].team, { teamId }));
      } else {
        const url = EXTERNAL_URLS[EXTERNAL_LEAGUE_KEYS[leagueKey]].franchiseProfile.replace(':teamId', teamId);
        window.open(url, '_blank', 'noreferrer');
      }
    } else {
      const playerId = String(newValue.data.id);
      if (isInternal) {
        navigate(generatePath(ROUTER_URLS[leagueKey].player, { playerId }));
      } else {
        const url = EXTERNAL_URLS[EXTERNAL_LEAGUE_KEYS[leagueKey]].playerProfile.replace(':playerId', playerId);
        window.open(url, '_blank', 'noreferrer');
      }
    }

    searchDebounce.clear();
    setInputValue('');
    setSearchValue('');
  };

  return (
    <StyledSearch>
      <StyledSearchIconWrapper>
        <SearchIcon sx={{ color: '#FFF' }} />
      </StyledSearchIconWrapper>
      <StyledAutocomplete
        id="header-search"
        data-testid="header-search"
        options={options}
        value={null}
        inputValue={inputValue}
        includeInputInList
        filterOptions={(x) => x}
        popupIcon={null}
        loading={isFetchingTeams || isFetchingPlayers}
        noOptionsText={
          // eslint-disable-next-line no-nested-ternary
          isTeamsError || isPlayersError
            ? 'Search failed. Please try again.'
            : searchEnabled ? 'No matches found.' : 'Search Players and Teams'
        }
        groupBy={(option: any) => (option.kind === 'team' ? 'Teams' : 'Players')}
        getOptionLabel={(option: any) => getOptionLabel(option)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>
              {params.group === 'Players' && displayPlayerYears ? (
                <Grid container>
                  <Grid item xs={8}>
                    <Typography component="span" fontWeight="600">Player</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography component="span" fontWeight="600">Years</Typography>
                  </Grid>
                </Grid>
              ) : params.group}
            </GroupHeader>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, option: any) => (
          <li {...props} key={`${option.kind}-${option.data.id}`}>
            {option.kind === 'player' && displayPlayerYears ? (
              // A common last name (e.g. "Williams") can return many players with
              // identical labels - showing the years they played is the same way
              // the Player Finder's own name filter disambiguates them. NBA/WNBA
              // don't carry this data, so they just show the plain label below.
              <Grid container sx={{ width: '100%' }}>
                <Grid item xs={8}>
                  <Typography component="span">{getOptionLabel(option)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  {formatPlayerSeasons(option.data)}
                </Grid>
              </Grid>
            ) : (
              <Typography component="span">{getOptionLabel(option)}</Typography>
            )}
          </li>
        )}
        renderInput={(params) => {
          const textFieldProps = {
            ...params,
            placeholder: 'Search Players and Teams',
            size: 'small' as const,
            inputProps: {
              ...params.inputProps,
              'aria-label': 'Search Players and Teams',
            },
            InputProps: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {(isFetchingTeams || isFetchingPlayers) ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          };
          return <TextField {...textFieldProps} />;
        }}
      />
    </StyledSearch>
  );
}

export default Search;
